import { TVNavParams } from '@euler/app/Routes.tv';
import { IconProps } from '@euler/app/components/icons/types';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { useSearchContext } from '@euler/app/tv/components/dashboard/SearchContext';
import { WiredSearchInput } from '@euler/app/tv/components/dashboard/SearchInput';
import { BookmarkIcon } from '@euler/app/tv/components/dashboard/icons/BookmarkIcon';
import { kDashboardHeaderHeight } from '@euler/app/tv/functions/constants';
import { useActiveTab } from '@euler/app/tv/functions/useActiveTabIndex';
import { useHomeTabNavigator } from '@euler/app/tv/functions/useHomeTabNavigator';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { PresentationTheme } from '@euler/app/tv/theme/types';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { FC, memo, useCallback } from 'react';
import { StyleProp, Text, TouchableOpacity, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { HistoryIcon } from './icons/HistoryIcon';
import { HomeIcon } from './icons/HomeIcon';
import { LocationIcon } from './icons/LocationIcon';
import { SearchIcon } from './icons/SearchIcon';
import { SettingsIcon } from './icons/SettingsIcon';

export const DashboardHeader = memo(
  ({
    storeName,
    logoUrl,
    style,
    onLogoPress,
    onHomePress,
    onHistoryPress,
    onSearchPress,
    onBookmarksPress,
    onSettingsPress,
  }: {
    storeName: string;
    logoUrl?: string;
    style?: StyleProp<ViewStyle>;
    onLogoPress?: () => void;
    onHomePress?: () => void;
    onHistoryPress?: () => void;
    onSearchPress?: () => void;
    onBookmarksPress?: () => void;
    onSettingsPress?: () => void;
  }) => {
    const activeTab = useActiveTab();
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const { query$ } = useSearchContext();
    const [paddingLeft, paddingRight] = useScreenContentPadding();
    return (
      <HStack
        css={`
          background-color: ${theme.dashboard.navbar.bg};
          padding-left: ${paddingLeft}px;
          padding-right: ${paddingRight}px;
          align-items: stretch;
        `}
        style={style}
      >
        {/** left */}
        <HStack justifyContent="flex-start">
          <TouchableOpacity onPress={onLogoPress}>
            <Img
              uri={logoUrl}
              css={`
                width: ${rpx(60)}px;
                height: ${rpx(60)}px;
                border-radius: ${rpx(60)}px;
              `}
            />
          </TouchableOpacity>

          {/** store name */}
          <HStack
            justifyContent="flex-start"
            css={`
              margin-left: ${rpx(64)}px;
            `}
          >
            <LocationIcon
              width={rpx(26)}
              height={rpx(28)}
              color={theme.dashboard.navbar.storeName.icon.color}
            />
            <Text
              css={`
                font-family: ${FontFamily.NotoSans.Regular};
                font-size: ${rpx(28)}px;
                line-height: ${rpx(42)}px;
                color: ${theme.dashboard.navbar.storeName.color};
                margin-left: ${rpx(13)}px;
              `}
            >
              {storeName}
            </Text>
          </HStack>

          <WiredSearchInput query$={query$} instanceSearchEnabled />
        </HStack>

        {/** right */}
        <HStack
          justifyContent="flex-end"
          css={`
            margin-right: ${-rpx(44)}px;
          `}
        >
          <RightIconButton
            theme={theme}
            Icon={HomeIcon}
            active={activeTab === 'Dashboard'}
            onPress={onHomePress}
          />
          <RightIconButton
            theme={theme}
            Icon={HistoryIcon}
            active={activeTab === 'History'}
            onPress={onHistoryPress}
          />
          <RightIconButton
            theme={theme}
            Icon={SearchIcon}
            active={activeTab === 'Search'}
            onPress={onSearchPress}
          />
          <RightIconButton
            theme={theme}
            Icon={BookmarkIcon}
            active={activeTab === 'Bookmarks'}
            onPress={onBookmarksPress}
          />
          <RightIconButton
            theme={theme}
            Icon={SettingsIcon}
            onPress={onSettingsPress}
          />
        </HStack>
      </HStack>
    );
  },
);

const RightIconButton = memo(
  ({
    theme,
    active,
    style,
    iconStyle,
    Icon,
    onPress,
  }: {
    theme: PresentationTheme;
    active?: boolean;
    style?: StyleProp<ViewStyle>;
    iconStyle?: StyleProp<ViewStyle>;
    onPress?: () => void;
    Icon: FC<
      IconProps & { active?: boolean; startColor?: string; endColor?: string }
    >;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        css={`
          width: ${rpx(44)}px;
          height: ${rpx(44)}px;
          align-items: center;
          justify-content: center;
          margin-left: ${rpx(72)}px;
        `}
        style={style}
        onPress={onPress}
      >
        <Icon
          style={iconStyle}
          active={active}
          size={rpx(44)}
          startColor={theme.dashboard.navbar.icon.activeStartColor}
          endColor={theme.dashboard.navbar.icon.activeEndColor}
          color={theme.dashboard.navbar.icon.inactiveColor}
        />
      </TouchableOpacity>
    );
  },
);

export const WiredDashboardHeader = memo(() => {
  const navigation = useNavigation<StackNavigationProp<TVNavParams>>();
  const tabNavigation = useHomeTabNavigator();
  const { rpx } = useResponsiveContext();

  const { top } = useSafeAreaInsets();

  const { org, store } = useCurrentUserDangerously();

  const onHomePress = useCallback(() => {
    tabNavigation.navigate('Dashboard', {});
  }, [tabNavigation]);

  const onHistoryPress = useCallback(() => {
    tabNavigation.navigate('History', {});
  }, [tabNavigation]);

  const onSearchPress = useCallback(() => {
    tabNavigation.navigate('Search', {});
  }, [tabNavigation]);

  const onSettingsPress = useCallback(() => {
    navigation.navigate('Settings', {});
  }, [navigation]);

  const onBookmarksPress = useCallback(() => {
    tabNavigation.navigate('Bookmarks', {});
  }, [tabNavigation]);

  const onLogoPress = useCallback(() => {
    navigation.navigate('StoreInfo', {});
  }, [navigation]);

  return (
    <DashboardHeader
      storeName={store.name}
      logoUrl={org.logoImgUrl ?? org.iconImgUrl}
      onLogoPress={onLogoPress}
      onHomePress={onHomePress}
      onHistoryPress={onHistoryPress}
      onSearchPress={onSearchPress}
      onBookmarksPress={onBookmarksPress}
      onSettingsPress={onSettingsPress}
      css={`
        height: ${rpx(kDashboardHeaderHeight) + top}px;
        padding-top: ${top}px;
      `}
    />
  );
});
